import {
  ACESFilmicToneMapping,
  AmbientLight,
  Box3,
  Color,
  DirectionalLight,
  Group,
  HemisphereLight,
  MathUtils,
  NoToneMapping,
  Object3D,
  PerspectiveCamera,
  Raycaster,
  Scene,
  SRGBColorSpace,
  Vector2,
  Vector3,
  WebGLRenderer,
} from "three";
import { MapControls } from "three/addons/controls/MapControls.js";
import { SceneOrigin } from "../geo/origin";

/** Free-orbit tilt limit for the normal 3D view — see `setPlanMode`. */
const MAX_POLAR_ANGLE_3D = MathUtils.degToRad(89);

export interface ViewerStats {
  fps: number;
  drawCalls: number;
  triangles: number;
  textures: number;
  geometries: number;
}

/**
 * three.js scene, camera and controls for a georeferenced site.
 *
 * Rendering is on demand: a frame is drawn when the camera moves or a layer reports new
 * content, which keeps an idle extension panel from burning GPU inside Trimble Connect.
 */
export class Viewer {
  readonly renderer: WebGLRenderer;
  readonly scene = new Scene();
  readonly camera: PerspectiveCamera;
  readonly controls: MapControls;
  readonly origin = new SceneOrigin();
  /** Everything georeferenced hangs off this group so vertical exaggeration is one scale. */
  readonly world = new Group();

  readonly sun: DirectionalLight;
  private readonly sky: HemisphereLight;
  private readonly ambient: AmbientLight;

  private readonly raycaster = new Raycaster();
  private readonly pointer = new Vector2();
  /**
   * Substitutes a real-world elevation for whatever a pick landed on, scoped to one group
   * (see {@link setHeightOverride}) — the flat terrain plate reports its true DEM height at
   * the picked east/north instead of the flattened one it's actually drawn at, so the
   * cursor readout and measurement tool see real relief even though the surface looks flat.
   */
  private heightOverride: {
    group: Object3D;
    sample: (east: number, north: number) => number | null;
    /** Local (unscaled) Y the group is actually drawn at — see {@link setHeightOverride}. */
    flatY: number;
  } | null = null;
  private needsRender = true;
  private continuous = 0;
  private running = false;
  private plan = false;
  private lastFrameTime = 0;
  private fps = 0;
  private resizeObserver?: ResizeObserver;

  constructor(readonly canvas: HTMLCanvasElement) {
    this.renderer = new WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: "high-performance",
      // The site model is opaque and fills the view; skipping the alpha channel saves
      // bandwidth on the compositor path.
      alpha: false,
      logarithmicDepthBuffer: true,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.toneMapping = NoToneMapping;

    this.scene.background = new Color(0x0e141b);
    this.scene.add(this.world);

    this.camera = new PerspectiveCamera(55, 1, 0.5, 20000);
    this.camera.position.set(60, 90, 120);

    this.controls = new MapControls(this.camera, canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.12;
    this.controls.screenSpacePanning = false;
    this.controls.maxPolarAngle = MAX_POLAR_ANGLE_3D;
    this.controls.minDistance = 0.5;
    this.controls.maxDistance = 12000;
    this.controls.zoomToCursor = true;
    this.controls.addEventListener("change", () => this.requestRender());

    // A hemisphere fill plus one directional key reads well on both draped orthophoto
    // terrain and untextured elevation surfaces without washing out the imagery.
    this.sky = new HemisphereLight(0xbcd3e8, 0x37414d, 1.1);
    this.ambient = new AmbientLight(0xffffff, 0.35);
    this.sun = new DirectionalLight(0xfff4e2, 1.7);
    this.setSun(315, 45);
    this.scene.add(this.sky, this.ambient, this.sun, this.sun.target);

    this.observeSize();
  }

  /** Positions the key light from a compass azimuth and elevation, both in degrees. */
  setSun(azimuthDeg: number, elevationDeg: number, distance = 1000): void {
    const azimuth = MathUtils.degToRad(azimuthDeg);
    const elevation = MathUtils.degToRad(elevationDeg);
    const horizontal = Math.cos(elevation) * distance;
    this.sun.position.set(
      Math.sin(azimuth) * horizontal,
      Math.sin(elevation) * distance,
      -Math.cos(azimuth) * horizontal,
    );
    this.sun.target.position.set(0, 0, 0);
    this.requestRender();
  }

  setLightIntensity(sun: number, ambient: number): void {
    this.sun.intensity = sun;
    this.ambient.intensity = ambient * 0.35;
    this.sky.intensity = ambient * 1.1;
    this.requestRender();
  }

  setToneMapping(filmic: boolean): void {
    this.renderer.toneMapping = filmic ? ACESFilmicToneMapping : NoToneMapping;
    this.renderer.toneMappingExposure = 1;
    this.scene.traverse(() => undefined);
    this.requestRender();
  }

  setBackground(color: number): void {
    (this.scene.background as Color).setHex(color);
    this.requestRender();
  }

  requestRender(): void {
    this.needsRender = true;
  }

  /**
   * Keeps rendering every frame until the returned function is called. Layers hold one of
   * these while streaming so newly arriving tiles appear without an input event.
   */
  holdContinuousRender(): () => void {
    this.continuous++;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.continuous--;
      this.requestRender();
    };
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    const loop = (time: number) => {
      if (!this.running) return;
      requestAnimationFrame(loop);
      const controlsChanged = this.controls.update();
      if (controlsChanged || this.needsRender || this.continuous > 0) {
        this.needsRender = false;
        this.updateClipPlanes();
        this.renderer.render(this.scene, this.camera);
        if (this.lastFrameTime) {
          const dt = time - this.lastFrameTime;
          if (dt > 0) this.fps = this.fps * 0.9 + (1000 / dt) * 0.1;
        }
        this.lastFrameTime = time;
      } else {
        this.lastFrameTime = 0;
      }
    };
    requestAnimationFrame(loop);
  }

  stop(): void {
    this.running = false;
  }

  /**
   * Keeps the depth range tight around the visible content. Sites span a few hundred
   * metres but the camera can pull back kilometres, and a fixed near plane of 0.5 m with a
   * 20 km far plane wastes precision even with a logarithmic buffer.
   */
  private updateClipPlanes(): void {
    const distance = this.camera.position.distanceTo(this.controls.target);
    const near = Math.max(0.05, distance / 5000);
    const far = Math.max(distance * 6, 2000);
    if (near !== this.camera.near || far !== this.camera.far) {
      this.camera.near = near;
      this.camera.far = far;
      this.camera.updateProjectionMatrix();
    }
  }

  /** Frames a world-space box, leaving a small margin. */
  frameBox(box: Box3, margin = 1.25): void {
    if (box.isEmpty()) return;
    const size = box.getSize(new Vector3());
    const center = box.getCenter(new Vector3());
    const radius = Math.max(size.length() / 2, 1);
    const fov = MathUtils.degToRad(this.camera.fov);
    const distance = (radius / Math.sin(fov / 2)) * margin;

    // Look from the south-east and above: matches how site photography is usually read.
    // In plan mode that tilt is locked out, so frame straight down instead.
    const direction = this.plan ? new Vector3(0, 1, 0.001) : new Vector3(0.55, 0.62, 0.55).normalize();
    this.camera.position.copy(center).addScaledVector(direction, distance);
    this.controls.target.copy(center);
    this.controls.maxDistance = Math.max(distance * 8, 2000);
    this.camera.updateProjectionMatrix();
    this.controls.update();
    this.requestRender();
  }

  get isPlanMode(): boolean {
    return this.plan;
  }

  /**
   * Toggles between the free-orbit 3D view and a locked top-down 2D plan view — same scene,
   * same camera, just a different constraint on the controls: plan mode snaps straight down
   * over the current target and disables tilt/rotate, leaving pan and zoom as the only ways
   * to move (i.e. it reads like a flat map rather than a 3D scene).
   */
  setPlanMode(enabled: boolean): void {
    this.plan = enabled;
    if (enabled) {
      const target = this.controls.target.clone();
      const distance = Math.max(this.camera.position.distanceTo(target), this.controls.minDistance);
      // A hair off dead-straight-down avoids the camera-look-direction singularity OrbitControls
      // hits at polar angle 0.
      this.camera.position.copy(target).add(new Vector3(0, distance, distance * 0.001 + 0.001));
      this.controls.minPolarAngle = 0;
      this.controls.maxPolarAngle = 0;
      this.controls.enableRotate = false;
    } else {
      this.controls.minPolarAngle = 0;
      this.controls.maxPolarAngle = MAX_POLAR_ANGLE_3D;
      this.controls.enableRotate = true;
    }
    this.controls.update();
    this.requestRender();
  }

  /**
   * Registers a real-world height lookup for picks landing inside `group`: the returned
   * point's height is replaced by `sample(east, north)` whenever it returns a finite value.
   * `flatY` is the local (unscaled) Y the group is actually *drawn* at — the terrain plate
   * is flat, so this is one constant — handed back by `pick()` alongside the real height so
   * callers that draw their own graphics against the terrain (the measurement tool, saved
   * measurement overlays) can sit their visuals flush with what's on screen instead of
   * floating at the real, unflattened elevation. One override is kept at a time — the
   * terrain layer is the only thing drawn flat, so it's the only thing that needs one.
   */
  setHeightOverride(group: Object3D, sample: (east: number, north: number) => number | null, flatY: number): void {
    this.heightOverride = { group, sample, flatY };
  }

  clearHeightOverride(): void {
    this.heightOverride = null;
  }

  /** The terrain's flat drawn height, in the local (unscaled) frame — see `setHeightOverride`.
   *  `null` when nothing is currently drawn flat (no terrain loaded). */
  get terrainDisplayY(): number | null {
    return this.heightOverride?.flatY ?? null;
  }

  /** Casts a ray from a client-space pointer position into the world group. */
  pick(clientX: number, clientY: number): { point: Vector3; distance: number; displayY: number | null } | null {
    const rect = this.canvas.getBoundingClientRect();
    this.pointer.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.pointer, this.camera);
    this.raycaster.far = this.camera.far;
    const hits = this.raycaster.intersectObject(this.world, true);
    for (const hit of hits) {
      if (!hit.object.visible) continue;
      const point = hit.point.clone();
      let displayY: number | null = null;
      if (this.heightOverride && isDescendantOf(hit.object, this.heightOverride.group)) {
        displayY = this.heightOverride.flatY;
        const local = this.world.worldToLocal(point.clone());
        const projected = this.origin.toProjected(local);
        const real = this.heightOverride.sample(projected.east, projected.north);
        if (real !== null && Number.isFinite(real)) {
          local.y = this.origin.sceneY(real);
          point.copy(this.world.localToWorld(local));
        }
      }
      return { point, distance: hit.distance, displayY };
    }
    return null;
  }

  stats(): ViewerStats {
    const info = this.renderer.info;
    return {
      fps: this.fps,
      drawCalls: info.render.calls,
      triangles: info.render.triangles,
      textures: info.memory.textures,
      geometries: info.memory.geometries,
    };
  }

  private observeSize(): void {
    const apply = () => {
      const width = this.canvas.clientWidth || 1;
      const height = this.canvas.clientHeight || 1;
      this.renderer.setSize(width, height, false);
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.requestRender();
    };
    apply();
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(apply);
      this.resizeObserver.observe(this.canvas);
    } else {
      window.addEventListener("resize", apply);
    }
  }

  dispose(): void {
    this.stop();
    this.resizeObserver?.disconnect();
    this.controls.dispose();
    this.renderer.dispose();
  }
}

/** True when `node` is `ancestor` itself or nested somewhere underneath it. */
function isDescendantOf(node: Object3D, ancestor: Object3D): boolean {
  for (let current: Object3D | null = node; current; current = current.parent) {
    if (current === ancestor) return true;
  }
  return false;
}
